import { useEffect, useMemo, useState } from 'react'
import {
  AML_CATEGORY_TABS,
  resolveResultsByCategory,
  formatThreatLevel
} from '../../utils/amlResultCategories'
import { getMatchCardHeadline, getMatchCardSubtitle } from '../../utils/amlMatchPresentation'
import GreenIdScreeningSummary from './GreenIdScreeningSummary'
import { isWatchlistAmlProvider } from '../../utils/sanitizeVendorBranding'

/**
 * Tabbed list of AML matches by category (PEP, Adverse Media, etc.).
 */
export default function AmlCategoryResultsTabs({
  results = [],
  resultsByCategory = null,
  selectedIndex = 0,
  onSelectIndex,
  compact = false,
  showProviderMeta = false,
  provider = null,
  greenidVerificationId = null,
  greenidOverallStatus = null,
  screeningSummary = null
}) {
  const grouped = useMemo(
    () => resolveResultsByCategory(results, resultsByCategory),
    [results, resultsByCategory]
  )

  const firstNonEmptyTab = useMemo(() => {
    const tab = AML_CATEGORY_TABS.find((t) => (grouped[t.key]?.length || 0) > 0)
    return tab?.key || 'pep'
  }, [grouped])

  const [activeTab, setActiveTab] = useState(firstNonEmptyTab)

  useEffect(() => {
    setActiveTab(firstNonEmptyTab)
  }, [firstNonEmptyTab, results])

  const activeList = grouped[activeTab] || []
  const totalCount = AML_CATEGORY_TABS.reduce((n, t) => n + (grouped[t.key]?.length || 0), 0)

  if (totalCount === 0) {
    return <div className="text-sm text-gray-500">No matches in this category after filtering.</div>
  }

  return (
    <div className="space-y-3">
      {showProviderMeta && isWatchlistAmlProvider(screeningSummary?.provider) && (
        <GreenIdScreeningSummary summary={screeningSummary} compact />
      )}
      {showProviderMeta && !screeningSummary && greenidVerificationId && (
        <div className="text-xs text-gray-500 border border-gray-200 rounded-md p-2 bg-gray-50">
          <span className="mr-3">
            Screening reference: <strong>{greenidVerificationId}</strong>
          </span>
          {greenidOverallStatus && (
            <span>
              Status: <strong>{formatThreatLevel(greenidOverallStatus)}</strong>
            </span>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-2">
        {AML_CATEGORY_TABS.map((tab) => {
          const count = grouped[tab.key]?.length || 0
          if (count === 0 && activeTab !== tab.key) return null
          return (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
              <span className={`ml-1.5 px-1.5 py-0.5 text-xs rounded ${activeTab === tab.key ? 'bg-primary-500' : 'bg-gray-100'}`}>
                {count}
              </span>
            </button>
          )
        })}
      </div>

      <div className={`space-y-2 ${compact ? 'max-h-96 overflow-y-auto' : ''}`}>
        {activeList.map((r, idx) => {
          const flatIdx = (() => {
            let offset = 0
            for (const tab of AML_CATEGORY_TABS) {
              const len = grouped[tab.key]?.length || 0
              if (tab.key === activeTab) return offset + idx
              offset += len
            }
            return idx
          })()
          const selected = selectedIndex === flatIdx
          return (
            <button
              key={r.id || `${activeTab}-${idx}`}
              type="button"
              onClick={() => onSelectIndex?.(flatIdx)}
              className={`w-full text-left border rounded-lg p-3 hover:shadow-sm transition-shadow ${
                selected ? 'border-blue-400 ring-1 ring-blue-200 bg-blue-50/40' : 'border-gray-200'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-gray-900 text-sm">{getMatchCardHeadline(r)}</div>
                {getMatchCardSubtitle(r) && (
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">{getMatchCardSubtitle(r)}</p>
                )}
                {typeof r.confidence !== 'undefined' && (
                  <div className="text-xs text-gray-600 shrink-0">{r.confidence}%</div>
                )}
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {r.matchKind && r.matchKind !== 'person' && (
                  <span className="text-xs px-1.5 py-0.5 rounded border bg-slate-50 text-slate-600">
                    {r.matchKind.replace(/_/g, ' ')}
                  </span>
                )}
                {r.riskCategory && (
                  <span className="text-xs px-1.5 py-0.5 rounded border bg-gray-50 text-gray-700">
                    {r.riskCategory}
                  </span>
                )}
                {r.riskLevel && (
                  <span className="text-xs px-1.5 py-0.5 rounded border bg-orange-50 text-orange-800">
                    {r.riskLevel}
                  </span>
                )}
              </div>
              {r.threatLevel && (
                <div className="mt-1 text-xs text-gray-500">{formatThreatLevel(r.threatLevel)}</div>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
